import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Brain, KeyRound, CheckCircle2, AlertTriangle, Save, RefreshCw,
  ArrowDown, ArrowUp, Eye, EyeOff, Coins, BarChart3,
} from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { DocAIProviderForm } from "./DocAIProviderForm";

const PROVIDER_META = {
  openai: {
    label: "OpenAI",
    hint: "GPT-4o-mini · Vision · Whisper-1",
    helpUrl: "https://platform.openai.com/api-keys",
    keyHint: "sk-…",
    multiline: false,
  },
  anthropic: {
    label: "Anthropic Claude",
    hint: "claude-haiku-4-5 · Vision",
    helpUrl: "https://console.anthropic.com/settings/keys",
    keyHint: "sk-ant-…",
    multiline: false,
  },
  gemini: {
    label: "Google Gemini",
    hint: "gemini-2.5-flash · Vision",
    helpUrl: "https://aistudio.google.com/app/apikey",
    keyHint: "AIza…",
    multiline: false,
  },
  google_vision: {
    label: "Google Cloud Vision",
    hint: "OCR – API-Schlüssel (AIza…, empfohlen) oder Service-Account-JSON",
    helpUrl: "https://console.cloud.google.com/apis/credentials",
    keyHint: 'AIza… (API-Schlüssel) oder {"type":"service_account",...}',
    multiline: true,
  },
  google_docai: {
    label: "Google Document AI",
    hint: "Invoice Parser (EU) – primäre Belegerkennung: Felder mit Konfidenz + Einzelpositionen",
    helpUrl: "https://console.cloud.google.com/ai/document-ai/processors",
    keyHint: "",
    multiline: true,
    custom: true,
  },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtCHF(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("de-CH", {
    style: "currency", currency: "CHF", minimumFractionDigits: v < 1 ? 4 : 2,
  }).format(v);
}

function ProviderRow({ provider, info, usageBucket, onSaved, onTested }) {
  const meta = PROVIDER_META[provider];
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/admin/ai-providers/${provider}`, { api_key: value });
      toast.success(`${meta.label} gespeichert`);
      setValue("");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm(`${meta.label}-Key entfernen?`)) return;
    setSaving(true);
    try {
      await apiClient.put(`/admin/ai-providers/${provider}`, { api_key: "" });
      toast.success(`${meta.label}-Key entfernt`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await apiClient.post(`/admin/ai-providers/${provider}/test`);
      if (r.data.ok) {
        toast.success(`${meta.label} OK · ${r.data.sample_output || ""}`);
      } else {
        toast.error(`${meta.label}: ${r.data.error}`);
      }
      onTested();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const InputCmp = meta.multiline ? Textarea : Input;

  return (
    <div className="p-4 rounded-lg border border-slate-200 bg-white" data-testid={`ai-provider-row-${provider}`}>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <KeyRound className="h-4 w-4 text-slate-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-slate-900">{meta.label}</div>
            <div className="text-[11px] text-slate-500 truncate">{meta.hint}</div>
          </div>
          {info?.is_set ? (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]" data-testid={`ai-provider-set-${provider}`}>
              <CheckCircle2 className="h-3 w-3 mr-0.5" /> Aktiv
            </Badge>
          ) : (
            <Badge variant="outline" className="border-slate-300 text-slate-500 bg-slate-50 text-[10px]">
              Nicht gesetzt
            </Badge>
          )}
          {info?.last_test_ok === true && (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]" title={`Letzter Test: ${fmtDateTime(info.last_test_at)}`}>
              Test OK
            </Badge>
          )}
          {info?.last_test_ok === false && (
            <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50 text-[10px]" title={info.last_test_error || ""}>
              <AlertTriangle className="h-3 w-3 mr-0.5" /> Test FAIL
            </Badge>
          )}
          {usageBucket && (
            <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 text-[10px]" title={`Letzter Test: ${fmtDateTime(info?.last_test_at)}`} data-testid={`ai-provider-cost-${provider}`}>
              <Coins className="h-3 w-3 mr-0.5 text-amber-500" />
              {usageBucket.total_calls} Calls · {fmtCHF(usageBucket.total_cost_chf)}
            </Badge>
          )}
        </div>
        <a
          href={meta.helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-sky-600 hover:underline flex-shrink-0"
          data-testid={`ai-provider-link-${provider}`}
        >
          Key beschaffen →
        </a>
      </div>

      {info?.is_set && info.preview && (
        <div className="text-[11px] text-slate-500 mb-2 font-mono" data-testid={`ai-provider-preview-${provider}`}>
          Aktuell hinterlegt: <span className="text-slate-700">{info.preview}</span>
        </div>
      )}
      {info?.active_model && (
        <div className="text-[11px] text-slate-500 mb-2" data-testid={`ai-provider-active-model-${provider}`}>
          Modell: <span className="font-mono text-violet-700 font-semibold">{info.active_model}</span>
          {info.model_checked_at && (
            <span className="text-slate-400 ml-2">
              · Auto-Update {fmtDateTime(info.model_checked_at)}
              {info.model_candidate_count ? ` · ${info.model_candidate_count} verfügbar` : ""}
            </span>
          )}
        </div>
      )}

      {meta.custom ? (
        <DocAIProviderForm info={info} onSaved={onSaved} />
      ) : (
      <div className="flex items-stretch gap-2">
        <div className="flex-1 relative">
          <InputCmp
            type={show || meta.multiline ? "text" : "password"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={meta.keyHint}
            className={meta.multiline ? "font-mono text-xs min-h-[80px]" : "pr-9 font-mono text-xs"}
            data-testid={`ai-provider-input-${provider}`}
          />
          {!meta.multiline && (
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
            >
              {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
        <Button onClick={save} disabled={!value.trim() || saving} size="sm" data-testid={`ai-provider-save-${provider}`}>
          <Save className="h-3.5 w-3.5 mr-1" /> Speichern
        </Button>
      </div>
      )}

      {info?.is_set && (
        <div className="flex items-center gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={test} disabled={testing} data-testid={`ai-provider-test-${provider}`}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${testing ? "animate-spin" : ""}`} />
            Live testen
          </Button>
          <Button variant="ghost" size="sm" onClick={clearKey} disabled={saving} className="text-rose-600 hover:text-rose-700" data-testid={`ai-provider-clear-${provider}`}>
            Entfernen
          </Button>
          {info.last_test_error && (
            <span className="text-[10px] text-rose-600 truncate flex-1" title={info.last_test_error}>
              {info.last_test_error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function AiProvidersCard() {
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusR, usageR] = await Promise.all([
        apiClient.get("/admin/ai-providers"),
        apiClient.get("/admin/ai-providers/usage"),
      ]);
      setStatus(statusR.data);
      setUsage(usageR.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Provider-Status nicht laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const usageByProvider = (usage?.by_provider || []).reduce((acc, p) => {
    acc[p.provider] = p;
    return acc;
  }, {});

  const movePriority = async (idx, dir) => {
    if (!status) return;
    const newPriority = [...status.priority];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= newPriority.length) return;
    [newPriority[idx], newPriority[newIdx]] = [newPriority[newIdx], newPriority[idx]];
    try {
      const r = await apiClient.put("/admin/ai-providers/priority", { priority: newPriority });
      setStatus(r.data);
      toast.success("Fallback-Reihenfolge aktualisiert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reihenfolge konnte nicht gespeichert werden");
    }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="ai-providers-card">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900">KI-Provider-Keys (System-weit)</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            const id = toast.loading("Modelle werden bei allen Providern abgefragt …");
            try {
              const r = await apiClient.post("/admin/ai-providers/refresh-models");
              const changed = r.data.results.filter((x) => x.changed);
              if (changed.length > 0) {
                toast.success(
                  `${changed.length} Modell(e) aktualisiert: `
                  + changed.map((x) => `${x.provider} → ${x.picked}`).join(", "),
                  { id },
                );
              } else {
                toast.success("Alle Modelle sind aktuell", { id });
              }
              await load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Refresh fehlgeschlagen", { id });
            }
          }} data-testid="ai-providers-refresh-models">
            <Brain className="h-4 w-4 mr-1" />
            Modelle prüfen
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ai-providers-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Neu laden
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Hier hinterlegte Keys gelten für <strong>alle Mandanten</strong>. Wenn der primäre
        Provider failed, springt das System automatisch zum nächsten in der Fallback-Reihenfolge.
      </p>

      {/* Iter79.5: Aktueller Monats-Verbrauch */}
      {usage && (
        <div className="mb-4 p-3 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between flex-wrap gap-3" data-testid="ai-usage-banner">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-900">
              Verbrauch {usage.year_month}:
            </span>
            <span className="text-sm font-mono text-slate-700" data-testid="ai-usage-total-calls">
              {usage.grand_total_calls} Calls
            </span>
            <span className="text-sm font-mono text-amber-700 font-semibold" data-testid="ai-usage-total-cost">
              <Coins className="h-3.5 w-3.5 inline mr-0.5 text-amber-500" />
              {fmtCHF(usage.grand_total_chf)}
            </span>
          </div>
          <div className="text-[10px] text-slate-400">
            Schätzung · USD/CHF Kurs {usage.usd_to_chf}
          </div>
        </div>
      )}

      <div className="grid gap-3 mb-5">
        {status && Object.entries(PROVIDER_META).map(([key]) => (
          <ProviderRow
            key={key}
            provider={key}
            info={status.providers[key]}
            usageBucket={usageByProvider[key]}
            onSaved={load}
            onTested={load}
          />
        ))}
      </div>

      {status && status.priority?.length > 0 && (
        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Text-LLM Fallback-Reihenfolge</h3>
          <p className="text-xs text-slate-500 mb-3">
            Belege-Analyse + WhatsApp-Bot probieren die Provider in dieser Reihenfolge durch.
            Pfeile zum Umsortieren — nur Provider mit hinterlegtem Key werden berücksichtigt.
          </p>
          <ol className="space-y-1" data-testid="ai-providers-priority-list">
            {status.priority.map((p, idx) => (
              <li key={p} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-100" data-testid={`ai-providers-priority-${idx}`}>
                <span className="font-mono text-xs text-slate-400 w-5">{idx + 1}.</span>
                <span className="font-medium text-slate-900 flex-1">{PROVIDER_META[p]?.label || p}</span>
                {status.providers[p]?.is_set ? (
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">aktiv</Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]">kein Key</Badge>
                )}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={idx === 0} onClick={() => movePriority(idx, -1)} data-testid={`ai-providers-up-${idx}`}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={idx === status.priority.length - 1} onClick={() => movePriority(idx, 1)} data-testid={`ai-providers-down-${idx}`}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
}
